import { ERROR_MESSAGES } from '../config/constants';
import logger from './logger';

class AppError extends Error {
    constructor(message, code, details = null) {
        super(message);
        this.code = code;
        this.details = details;
        this.name = 'AppError';
    }
}

const errorHandler = {
    // Handle API errors
    handleApiError: (error) => {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const { status, data } = error.response;
            
            switch (status) {
                case 400:
                    return new AppError(data.error || ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG, 'BAD_REQUEST', data.details);
                case 401:
                    return new AppError(ERROR_MESSAGES.GENERAL.UNAUTHORIZED, 'UNAUTHORIZED');
                case 403:
                    return new AppError(ERROR_MESSAGES.GENERAL.UNAUTHORIZED, 'FORBIDDEN');
                case 404:
                    return new AppError(ERROR_MESSAGES.GENERAL.NOT_FOUND, 'NOT_FOUND');
                case 500:
                    return new AppError(ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG, 'SERVER_ERROR');
                default:
                    return new AppError(data.error || ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG, 'UNKNOWN_ERROR');
            }
        } else if (error.request) {
            // The request was made but no response was received
            return new AppError('No response from server. Please check your connection.', 'NETWORK_ERROR');
        } else {
            // Something happened in setting up the request that triggered an Error
            return new AppError(error.message || ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG, 'REQUEST_ERROR');
        }
    },

    // Handle database errors
    handleDatabaseError: (error) => {
        return new AppError('Database operation failed. Please try again.', 'DATABASE_ERROR');
    },

    // Handle authentication errors
    handleAuthError: (error) => {
        switch (error.code) {
            case 'auth/invalid-login-credentials':
                return {
                    message: 'Invalid email or password. Please check your credentials and try again.',
                    code: error.code
                };
            case 'auth/user-not-found':
                return {
                    message: 'No account found with this email address.',
                    code: error.code
                };
            case 'auth/wrong-password':
                return {
                    message: 'Incorrect password. Please try again.',
                    code: error.code
                };
            case 'auth/email-already-in-use':
                return {
                    message: 'This email is already registered. Please use a different email or sign in.',
                    code: error.code
                };
            case 'auth/weak-password':
                return {
                    message: 'Password should be at least 6 characters long.',
                    code: error.code
                };
            case 'auth/invalid-email':
                return {
                    message: 'Please enter a valid email address.',
                    code: error.code
                };
            case 'auth/too-many-requests':
                return {
                    message: 'Too many failed attempts. Please try again later.',
                    code: error.code
                };
            case 'auth/network-request-failed':
                return {
                    message: 'Network error. Please check your internet connection.',
                    code: error.code
                };
            default:
                return {
                    message: 'An error occurred. Please try again.',
                    code: error.code
                };
        }
    },

    // Handle file upload errors
    handleFileError: (error) => {
        if (error.code === 'FILE_TOO_LARGE') {
            return new AppError('File size exceeds the limit. Please upload a smaller file.', 'FILE_TOO_LARGE');
        }
        if (error.code === 'INVALID_FILE_TYPE') {
            return new AppError('Invalid file type. Please check the allowed file types.', 'INVALID_FILE_TYPE');
        }
        return new AppError('File upload failed. Please try again.', 'FILE_UPLOAD_ERROR');
    },

    // Handle validation errors
    handleValidationError: (errors) => {
        return new AppError('Validation failed', 'VALIDATION_ERROR', errors);
    },

    // Handle Firebase errors
    handleFirebaseError: (error) => {
        let message = ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG;
        let code = error.code || 'FIREBASE_ERROR';

        // First check for Firebase Functions V2 specific errors
        if (error.details) {
            // Preserve original error code if it exists in the details
            code = error.details.code || code;
            
            // If it's a not-found error
            if (code === 'not-found') {
                message = error.message || 'The requested resource was not found';
                return new AppError(message, code, error.details);
            }
        }

        if (error.code) {
            switch (error.code) {
                case 'permission-denied':
                    message = 'You do not have permission to perform this action';
                    code = 'FIREBASE_PERMISSION_DENIED';
                    break;
                case 'unavailable':
                    message = 'Firebase service is currently unavailable. Please try again later';
                    code = 'FIREBASE_UNAVAILABLE';
                    break;
                case 'network-error':
                    message = 'Network error occurred. Please check your connection';
                    code = 'FIREBASE_NETWORK_ERROR';
                    break;
                case 'index-not-defined':
                    message = 'Database query requires an index. Please contact support';
                    code = 'FIREBASE_INDEX_ERROR';
                    break;
                case 'invalid-argument':
                    message = 'Invalid data provided';
                    code = 'FIREBASE_INVALID_ARGUMENT';
                    break;
                case 'data-stale':
                    message = 'Data is out of date. Please refresh and try again';
                    code = 'FIREBASE_DATA_STALE';
                    break;
                case 'functions/unauthenticated':
                    message = 'You must be logged in to perform this action';
                    code = 'FIREBASE_UNAUTHENTICATED';
                    break;
                case 'functions/internal':
                    message = 'An internal error occurred. Please try again later';
                    code = 'FIREBASE_INTERNAL_ERROR';
                    break;
                case 'functions/invalid-argument':
                    message = 'Invalid data provided';
                    code = 'FIREBASE_INVALID_ARGUMENT';
                    break;
                case 'functions/not-found':
                    message = 'The requested resource was not found';
                    code = 'not-found'; // Use consistent code
                    break;
                case 'functions/failed-precondition':
                    message = 'Operation cannot be executed in the current system state';
                    code = 'FIREBASE_FAILED_PRECONDITION';
                    break;
                case 'functions/out-of-range':
                    message = 'Operation was attempted past the valid range';
                    code = 'FIREBASE_OUT_OF_RANGE';
                    break;
                case 'functions/unimplemented':
                    message = 'Operation is not implemented or not supported';
                    code = 'FIREBASE_UNIMPLEMENTED';
                    break;
                case 'functions/unavailable':
                    message = 'Service is currently unavailable';
                    code = 'FIREBASE_UNAVAILABLE';
                    break;
                case 'functions/data-loss':
                    message = 'Unrecoverable data loss or corruption';
                    code = 'FIREBASE_DATA_LOSS';
                    break;
            }
        }

        return new AppError(
            error.message || message, 
            code, 
            process.env.NODE_ENV === 'development' ? error.details || null : null
        );
    },

    // Log error and return user-friendly message
    processError: (error) => {
        // If it's already an AppError, return it
        if (error instanceof AppError) {
            return error;
        }

        // Handle different types of errors
        if (error.response) {
            return errorHandler.handleApiError(error);
        }
        if (error.code && error.code.startsWith('auth/')) {
            return errorHandler.handleAuthError(error);
        }
        if (error.code && error.code.startsWith('FILE_')) {
            return errorHandler.handleFileError(error);
        }
        if (error.code && (error.code.startsWith('FIREBASE_') || error.code.includes('permission-denied') || error.code.includes('unavailable'))) {
            return errorHandler.handleFirebaseError(error);
        }

        // Default error
        return new AppError(
            ERROR_MESSAGES.GENERAL.SOMETHING_WENT_WRONG,
            'UNKNOWN_ERROR',
            process.env.NODE_ENV === 'development' ? error.message : null
        );
    }
};

export { errorHandler, AppError }; 